import { CURRENT_COOKIE_CONSENT_VERSION } from './consentVersion';
import { deleteCookie, getCookie, setCookie } from './cookieStorage';
import { sharedCookieDomainForHost } from './sharedCookieDomain';
import {
  CONSENT_COOKIE_NAME,
  LEGACY_THEME_STORAGE_KEY,
  THEME_COOKIE_NAME,
  type CookieConsentState,
} from './types';

export function loadConsent(): CookieConsentState | null {
  const raw = getCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CookieConsentState;
    if (parsed.version !== CURRENT_COOKIE_CONSENT_VERSION) return null;
    if (typeof parsed.preferences !== 'boolean' || typeof parsed.analytics !== 'boolean') {
      return null;
    }
    return { ...parsed, preferences: true };
  } catch {
    return null;
  }
}

export function hasDecided(): boolean {
  return loadConsent() !== null;
}

export function saveConsent(choices: Pick<CookieConsentState, 'analytics'>): CookieConsentState {
  const state: CookieConsentState = {
    version: CURRENT_COOKIE_CONSENT_VERSION,
    preferences: true,
    analytics: choices.analytics,
    decidedAt: new Date().toISOString(),
  };
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(state));
  return state;
}

/** Move legacy localStorage theme into the essential theme cookie. */
export function migrateLegacyThemeFromLocalStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    if (!raw) return null;
    setCookie(THEME_COOKIE_NAME, raw);
    window.localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
    return raw;
  } catch {
    return null;
  }
}

export function readThemeCookie(): string | null {
  return getCookie(THEME_COOKIE_NAME);
}

export function writeThemeCookie(value: string): void {
  const domain =
    typeof window !== 'undefined' ? sharedCookieDomainForHost(window.location.hostname) : undefined;
  if (domain) {
    setCookie(THEME_COOKIE_NAME, value, { domain });
  } else {
    setCookie(THEME_COOKIE_NAME, value);
  }
}

export function clearThemeCookie(): void {
  deleteCookie(THEME_COOKIE_NAME);
  if (typeof window !== 'undefined') {
    const domain = sharedCookieDomainForHost(window.location.hostname);
    if (domain) deleteCookie(THEME_COOKIE_NAME, domain);
  }
}
